import React, { ReactNode } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';
import { Badge, Variants } from '../ui/shadcn-ui/badge';
import { Button } from '../ui/shadcn-ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/shadcn-ui/dropdown-menu';
import { _cloneArray } from '../../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './TableComponents';
import dayjs from 'dayjs';

export function DataTable<TData>({
  columns,
  data,
  subMenuOptions,
  columnButton,
  emptyState,
}: DataTableProps<TData>) {
  function getFormattedColumns() {
    let column = _cloneArray(columns);

    column = column.map((col) => {
      col.header = col.label;
      if (col.headerClassNames) {
        col.header = () => (
          <div className={`${col.headerClassNames}`}>{col.label}</div>
        );
      }

      if (col.cellType === 'date') {
        col.cell = ({ row }) => {
          console.log('Row data:', row.original);
          const value = row.getValue(col.accessorKey) as string | null;
          console.log('nextSittingDate:', value);
          const formatted = value ? dayjs(value).format('MM-DD-YYYY') : 'N/A';
          return (
            <div className={`${col.cellClassNames ? col.cellClassNames : ''}`}>
              {formatted}
            </div>
          );
        };
      } else if (col.cellType === 'currency') {
        col.cell = ({ row }) => {
          const value = row.getValue(col.accessorKey);
          const amount = parseFloat(value as string);
          const formatted = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(amount);
          return (
            <div className={`${col.cellClassNames ? col.cellClassNames : ''}`}>
              {formatted}
            </div>
          );
        };
      } else if (col.cellType === 'tag') {
        col.cell = ({ row }) => {
          const value = row.getValue(col.accessorKey);
          const formattedTag = value as Variants;
          return (
            <div className={`${col.cellClassNames ? col.cellClassNames : ''}`}>
              <Badge variant={formattedTag}>{value as string}</Badge>
            </div>
          );
        };
      }

      if (col.accessorKey === 'button' && columnButton) {
        col.cell = () => (
          <Button
            variant='outline'
            className='w-min h-6 px-2 py-1 text-[#099250] text-xs font-semibold font-sarabun-bold border-[#D0D5DD]'
          >
            Download
          </Button>
        );
      }

      if (col.accessorKey === 'action' && subMenuOptions && subMenuOptions.length) {
        col.cell = ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' className='h-8 w-8 p-0'>
                <span className='sr-only'>Open menu</span>
                <MoreHorizontal className='h-4 w-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              {subMenuOptions.map((option) => (
                <React.Fragment key={option.text}>
                  <DropdownMenuItem
                    className='cursor-pointer'
                    onClick={() => option.onClick(row.original)}
                  >
                    {option.text}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className='last:hidden' />
                </React.Fragment>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      }

      return col;
    });

    return column;
  }

  const formattedColumns = getFormattedColumns();
  const table = useReactTable({
    data,
    columns: formattedColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className='rounded-md border md:border-none md:rounded-none'>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              className='bg-[#F3F3F3] hover:bg-[#F3F3F3] border-none rounded-[4px] whitespace-nowrap'
              key={headerGroup.id}
            >
              {headerGroup.headers.map((header) => (
                <TableHead
                  className='h-10 text-sm leading-4 font-semibold bg-brown text-white capitalize'
                  key={header.id}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                className='border-[#F3F3F3]'
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    className='py-[10px] text-[#1D283A] text-sm'
                    key={cell.id}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className='h-[300px] bg-[#FCFCFD]'
              >
                <div className='flex w-full h-full justify-center flex-col text-center'>
                  <h6 className='text-[#1D283A] text-sm font-semibold mt-3 font-jakarta-medium'>
                    {emptyState ?? 'No data available'}
                  </h6>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}

interface DataTableProps<TData> {
  columns: Column<TData>[];
  data: TData[];
  subMenuOptions?: { text: string; onClick: (field: TData) => void }[];
  children?: ReactNode;
  columnButton?: boolean;
  emptyState?: string;
}

export type Column<TData> = {
  accessorKey: string;
  headerClassNames?: string;
  cellClassNames?: string;
  cellType?: 'date' | 'currency' | 'tag' | undefined;
  label: string;
} & ColumnDef<TData>;
